import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { waLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { WhatsAppIcon } from "@/components/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = ["USA", "UK", "Canada", "Australia", "UAE / Dubai", "Singapore", "Europe", "India (outside Hyderabad)", "Other"];
const PROPERTY_TYPES = ["Apartment", "Villa", "Independent House", "Commercial Property", "Plot / Land", "Other"];
const OCCUPANCY = ["Owner occupied", "Tenant occupied", "Vacant", "Under construction", "Other"];
const SERVICE_OPTIONS = [
  "Tenant Finding", "Rent Collection", "Tenant Management", "Property Inspection", "Maintenance",
  "Cleaning", "Painting", "Plumbing", "Electrical", "Carpentry", "Interior Works", "Rental Agreement",
  "Tenant Verification", "Move-in", "Move-out", "Property Make-Ready", "Complete Property Management", "Other",
];

const initialForm = {
  name: "", country: "", whatsapp: "", email: "",
  property_location: "", locality: "", property_type: "", size: "", occupancy: "",
  services: [], message: "", website: "",
};

const inputCls =
  "w-full rounded-xl border border-navy-900/15 bg-white px-4 py-3 text-sm text-navy-900 placeholder:text-navy-900/35 outline-none transition-[border-color,box-shadow] focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25";

const Label = ({ children, required }) => (
  <label className="mb-1.5 block font-display text-xs font-bold uppercase tracking-[0.14em] text-navy-900/70">
    {children} {required && <span className="text-gold-600">*</span>}
  </label>
);

const FieldError = ({ id, msg }) =>
  msg ? <p className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`${id}-error`} role="alert">{msg}</p> : null;

export const LeadForm = ({ source = "website" }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: null });
  };

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!form.country) e.country = "Please select your country of residence.";
    if (!/^[0-9+\-\s()]{6,20}$/.test(form.whatsapp.trim())) e.whatsapp = "Please enter a valid WhatsApp number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...form, source });
      setDone(data.id);
      track("form_submit", { source });
      track("assessment_request", { location: "lead_form" });
      toast.success("Request received. We'll reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or message us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl border border-gold-500/30 bg-white p-8 text-center shadow-sm sm:p-12" data-testid="lead-form-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-status" />
        <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
          Request received.
        </h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
          Thank you, {form.name.split(" ")[0]}. A property manager will contact you on WhatsApp or email shortly
          to understand your property and arrange your free assessment.
        </p>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] text-navy-900/40">Reference: {done.slice(0, 8)}</p>
        <a
          href={waLink("Hello NRI Property Services, I just submitted an enquiry on your website and would like to discuss my property in Hyderabad.")}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold mt-8"
          data-testid="success-whatsapp-button"
          onClick={() => track("whatsapp_click", { location: "form_success" })}
        >
          <WhatsAppIcon className="h-4 w-4" /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-3xl border border-navy-900/10 bg-white p-6 shadow-sm sm:p-10" data-testid="lead-form">
      {/* Honeypot — invisible to humans */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }}
      />

      <fieldset>
        <legend className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-gold-600">Personal Details</legend>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <Label required>Full Name</Label>
            <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" data-testid="lead-form-name" autoComplete="name" />
            <FieldError id="lead-form-name" msg={errors.name} />
          </div>
          <div>
            <Label required>Country of Residence</Label>
            <select className={inputCls} value={form.country} onChange={set("country")} data-testid="lead-form-country">
              <option value="">Select country</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <FieldError id="lead-form-country" msg={errors.country} />
          </div>
          <div>
            <Label required>WhatsApp Number</Label>
            <input className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="+1 ... / +44 ... / +971 ..." data-testid="lead-form-whatsapp" autoComplete="tel" inputMode="tel" />
            <FieldError id="lead-form-whatsapp" msg={errors.whatsapp} />
          </div>
          <div>
            <Label required>Email Address</Label>
            <input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="lead-form-email" autoComplete="email" inputMode="email" />
            <FieldError id="lead-form-email" msg={errors.email} />
          </div>
        </div>
      </fieldset>

      <fieldset className="mt-9">
        <legend className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-gold-600">Property Details</legend>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <Label>Property Location</Label>
            <input className={inputCls} value={form.property_location} onChange={set("property_location")} placeholder="e.g. Hyderabad" data-testid="lead-form-location" />
          </div>
          <div>
            <Label>Locality</Label>
            <input className={inputCls} value={form.locality} onChange={set("locality")} placeholder="e.g. Kondapur, Madhapur, Gachibowli" data-testid="lead-form-locality" />
          </div>
          <div>
            <Label>Property Type</Label>
            <select className={inputCls} value={form.property_type} onChange={set("property_type")} data-testid="lead-form-property-type">
              <option value="">Select type</option>
              {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label>BHK / Size</Label>
            <input className={inputCls} value={form.size} onChange={set("size")} placeholder="e.g. 3 BHK · 1,650 sq ft" data-testid="lead-form-size" />
          </div>
          <div className="sm:col-span-2">
            <Label>Occupancy</Label>
            <div className="flex flex-wrap gap-2" data-testid="lead-form-occupancy">
              {OCCUPANCY.map((o) => (
                <button
                  type="button"
                  key={o}
                  onClick={() => { setForm({ ...form, occupancy: o }); }}
                  data-testid={`occupancy-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-full border px-4 py-2 font-display text-xs font-bold transition-[background-color,border-color,color] ${
                    form.occupancy === o
                      ? "border-navy-900 bg-navy-900 text-white"
                      : "border-navy-900/15 bg-white text-navy-900/70 hover:border-navy-900/40"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset className="mt-9">
        <legend className="font-display text-sm font-extrabold uppercase tracking-[0.2em] text-gold-600">Services Required</legend>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="lead-form-services">
          {SERVICE_OPTIONS.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => toggleService(s)}
              aria-pressed={form.services.includes(s)}
              data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full border px-4 py-2 font-display text-xs font-bold transition-[background-color,border-color,color] ${
                form.services.includes(s)
                  ? "border-gold-500 bg-gold-500 text-navy-900"
                  : "border-navy-900/15 bg-white text-navy-900/70 hover:border-gold-500/60"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mt-9">
        <Label>Tell us what you need help with</Label>
        <textarea
          className={`${inputCls} min-h-[110px] resize-y`}
          value={form.message}
          onChange={set("message")}
          placeholder="e.g. My tenant is vacating next month and I need the flat cleaned, painted and re-rented."
          data-testid="lead-form-message"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="lead-form-submit"
        className="btn-gold mt-8 w-full !py-4 text-base disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
        {loading ? "Submitting…" : "Request Free Assessment"}
      </button>

      <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-status" />
        Your details are used only to respond to your enquiry. We never ask for passwords, OTPs, Aadhaar or bank details through this form.
      </p>
    </form>
  );
};
